// Minimal remark plugin: auto-nest list items that follow a bold header bullet
// Pattern it handles:
// - li: "**Header:**" (colon may be inside or immediately after the bold)
// - subsequent li items (until next header) become a nested list under that header li
// This is conservative and only rewrites when the first li is clearly a header.

// We keep types loose to avoid adding new type deps.
export default function remarkNestHeaderBullets() {
  return function transformer(tree: any) {
    if (!tree || !Array.isArray(tree.children)) return;

    const visit = (node: any) => {
      if (!node) return;
      if (node.type === 'list' && Array.isArray(node.children)) {
        rewriteList(node);
      }
      if (Array.isArray(node.children)) {
        node.children.forEach(visit);
      }
    };

    const isHeaderListItem = (li: any): boolean => {
      if (!li || li.type !== 'listItem' || !Array.isArray(li.children) || li.children.length === 0) return false;
      const first = li.children[0];
      if (!first || first.type !== 'paragraph' || !Array.isArray(first.children) || first.children.length === 0) return false;
      // Case 1: Entire paragraph is a single strong node (possibly with colon inside)
      if (first.children.length === 1 && first.children[0].type === 'strong') {
        const text = toText(first.children[0]).trim();
        return text.endsWith(':') || text.length > 0;
      }
      // Case 2: strong followed by a colon text node only
      if (first.children.length === 2 && first.children[0].type === 'strong' && first.children[1].type === 'text') {
        const trailing = String(first.children[1].value || '').trim();
        const restIsColon = trailing === ':' || trailing === '：';
        // Ensure no other content in paragraph
        const strongText = toText(first.children[0]).trim();
        return strongText.length > 0 && restIsColon;
      }
      return false;
    };

    const toText = (node: any): string => {
      if (!node) return '';
      if (node.type === 'text') return String(node.value || '');
      if (Array.isArray(node.children)) return node.children.map(toText).join('');
      return '';
    };

    const rewriteList = (list: any) => {
      const items = list.children as any[];
      if (!items || items.length === 0) return;

      const rewritten: any[] = [];
      let i = 0;
      while (i < items.length) {
        const current = items[i];
        if (isHeaderListItem(current)) {
          // Collect subsequent non-header items
          const group: any[] = [];
          let j = i + 1;
          while (j < items.length && !isHeaderListItem(items[j])) {
            group.push(items[j]);
            j++;
          }
          // Only nest if there is at least one following item
          if (group.length > 0) {
            const nestedList = {
              type: 'list',
              ordered: list.ordered || false,
              spread: false,
              children: group
            };
            // Append nested list to the header list item
            current.children = Array.isArray(current.children) ? [...current.children, nestedList] : [nestedList];
            rewritten.push(current);
            i = j;
            continue;
          }
        }
        // Default: push single item
        rewritten.push(current);
        i++;
      }

      list.children = rewritten;
    };

    visit(tree);
  };
}


